import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
// @mui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MFA_ERROR_CODES, MFA_SETTINGS } from '@siptel-web/common';
import jwtDecode from 'jwt-decode';
import { toast } from 'react-toastify';

// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { createMessageError } from 'src/utils/messaggeError';

// ----------------------------------------------------------------------

const GENERIC_ERROR_MESSAGE =
  'Ha ocurrido un error, comuniquese con soporte si el error persiste.';
const TOO_MANY_REQUESTS_MESSAGE =
  'Demasiadas solicitudes. Intente nuevamente en unos minutos.';
const SESSION_EXPIRED_MESSAGE =
  'La sesión de verificación expiró. Inicie sesión nuevamente.';

const CODE_LENGTH = MFA_SETTINGS.CODE_LENGTH;
const EMPTY_DIGITS = Array(CODE_LENGTH).fill('');

// Errores que terminan la sesión de verificación: se vuelve a credenciales.
const SESSION_ENDING_ERRORS = [
  MFA_ERROR_CODES.SESSION_INVALID,
  MFA_ERROR_CODES.SESSION_EXPIRED,
  MFA_ERROR_CODES.ACCOUNT_BLOCKED,
  MFA_ERROR_CODES.ACCOUNT_INACTIVE,
  MFA_ERROR_CODES.EMAIL_NOT_CONFIGURED,
];

// Errores que invalidan el código actual: hay que solicitar uno nuevo.
const CODE_LOCKING_ERRORS = [
  MFA_ERROR_CODES.CODE_EXPIRED,
  MFA_ERROR_CODES.CODE_MAX_ATTEMPTS,
];

const getSessionExpiresAt = (sessionTemp, expiresAt) => {
  if (expiresAt) return expiresAt;
  try {
    const { exp } = jwtDecode(sessionTemp);
    return exp ? exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

// ----------------------------------------------------------------------

MfaCodeDialog.propTypes = {
  sessionTemp: PropTypes.string.isRequired,
  expiresAt: PropTypes.number,
  onBack: PropTypes.func.isRequired,
  onSessionEnded: PropTypes.func.isRequired,
};

// Modal del segundo paso del login (design §2, revisión 2026-10-07). Se monta
// solo mientras hay desafío MFA: al desmontarse se descarta todo su estado.
export default function MfaCodeDialog({
  sessionTemp,
  expiresAt,
  onBack,
  onSessionEnded,
}) {
  const { verifyMfa, resendMfa } = useAuth();

  const isMountedRef = useIsMountedRef();
  const inputsRef = useRef([]);
  // Ref para no reprogramar el timer de sesión si el padre recrea el callback.
  const onSessionEndedRef = useRef(onSessionEnded);
  onSessionEndedRef.current = onSessionEnded;

  const [digits, setDigits] = useState(EMPTY_DIGITS);
  // Arranca en 60 s: el código se acaba de enviar en el paso 1.
  const [cooldown, setCooldown] = useState(
    MFA_SETTINGS.RESEND_COOLDOWN_SECONDS,
  );
  const [resendsRemaining, setResendsRemaining] = useState(
    MFA_SETTINGS.MAX_RESENDS_PER_SESSION,
  );
  const [codeLocked, setCodeLocked] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const code = digits.join('');

  // Cuenta regresiva del reenvío (1 tick por segundo, limpiado en unmount).
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        setCooldown((value) => Math.max(value - 1, 0));
      }
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [cooldown, isMountedRef]);

  // Vencimiento de la session_temp → volver a credenciales.
  useEffect(() => {
    const sessionExpiresAt = getSessionExpiresAt(sessionTemp, expiresAt);
    if (!sessionExpiresAt) return undefined;
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        onSessionEndedRef.current(SESSION_EXPIRED_MESSAGE);
      }
    }, Math.max(sessionExpiresAt - Date.now(), 0));
    return () => clearTimeout(timeoutId);
  }, [sessionTemp, expiresAt, isMountedRef]);

  const focusDigit = (index) => {
    const input = inputsRef.current[index];
    if (input) input.focus();
  };

  const resetCode = () => {
    setDigits(EMPTY_DIGITS);
    setTimeout(() => focusDigit(0), 0);
  };

  // Escribe `value` a partir de la casilla `index` (un dígito tecleado, o el
  // código completo pegado/autocompletado) y mueve el foco a la siguiente.
  const fillDigitsFrom = (index, value) => {
    const chars = onlyDigits(value)
      .slice(0, CODE_LENGTH - index)
      .split('');
    setDigits((prev) => {
      const next = [...prev];
      chars.forEach((char, offset) => {
        next[index + offset] = char;
      });
      return next;
    });
    focusDigit(Math.min(index + chars.length, CODE_LENGTH - 1));
  };

  const handleDigitChange = (index, value) => {
    if (inlineError) setInlineError('');
    if (!onlyDigits(value)) {
      setDigits((prev) => prev.map((d, i) => (i === index ? '' : d)));
      return;
    }
    // Si la casilla ya tenía dígito, el nuevo es el último tecleado.
    const typed = onlyDigits(value);
    fillDigitsFrom(
      index,
      typed.length === 2 && digits[index] ? typed.slice(-1) : typed,
    );
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      focusDigit(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      focusDigit(index - 1);
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      focusDigit(index + 1);
    }
  };

  // Pegar siempre llena desde la primera casilla.
  const handlePaste = (event) => {
    const pasted = onlyDigits(event.clipboardData.getData('text'));
    if (!pasted) return;
    event.preventDefault();
    if (inlineError) setInlineError('');
    setDigits(EMPTY_DIGITS);
    fillDigitsFrom(0, pasted);
  };

  // Manejo común de errores de verify/resend (design §5). Devuelve true si
  // la sesión terminó (el componente se desmontará).
  const handleError = (error) => {
    console.error(error);
    const mfaError = error?.mfa_error;
    const message = typeof error?.message === 'string' ? error.message : '';

    if (SESSION_ENDING_ERRORS.includes(mfaError) || error?.statusCode === 401) {
      onSessionEnded(message || SESSION_EXPIRED_MESSAGE);
      return true;
    }

    if (CODE_LOCKING_ERRORS.includes(mfaError)) {
      setInlineError(message);
      setCodeLocked(true);
      return false;
    }

    switch (mfaError) {
      case MFA_ERROR_CODES.CODE_INCORRECT:
        setInlineError(message);
        resetCode();
        return false;
      case MFA_ERROR_CODES.RESEND_COOLDOWN:
        setCooldown(
          Number(error.retry_after_seconds) ||
            MFA_SETTINGS.RESEND_COOLDOWN_SECONDS,
        );
        toast.info(message);
        return false;
      case MFA_ERROR_CODES.RESEND_LIMIT:
        setResendsRemaining(0);
        toast.error(message);
        return false;
      case MFA_ERROR_CODES.EMAIL_SEND_FAILED:
        // El backend no consumió el reenvío ni el cooldown.
        toast.error(message || GENERIC_ERROR_MESSAGE);
        return false;
      default:
        break;
    }

    if (error?.statusCode === 429) {
      toast.error(TOO_MANY_REQUESTS_MESSAGE);
    } else if (!error?.statusCode || error.statusCode >= 500) {
      toast.error(GENERIC_ERROR_MESSAGE);
    } else {
      toast.error(createMessageError(message) || GENERIC_ERROR_MESSAGE);
    }
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // El Dialog vive en un portal, pero el evento de React sigue burbujeando
    // por el árbol de componentes: no debe llegar a ningún form padre.
    event.stopPropagation();
    if (codeLocked || verifying || resending) return;
    if (code.length !== CODE_LENGTH) {
      setInlineError(`Ingrese los ${CODE_LENGTH} dígitos del código`);
      return;
    }
    setInlineError('');
    setVerifying(true);
    try {
      // En éxito el contexto completa el login y la página redirige.
      await verifyMfa({ sessionTemp, code });
    } catch (error) {
      if (!isMountedRef.current) return;
      if (handleError(error)) return;
    }
    if (isMountedRef.current) setVerifying(false);
  };

  const handleResend = async () => {
    if (resending || verifying || cooldown > 0 || resendsRemaining === 0) {
      return;
    }
    setResending(true);
    try {
      const data = await resendMfa({ sessionTemp });
      if (!isMountedRef.current) return;
      toast.success(data?.message || 'Se envió un nuevo código a su correo.');
      setCooldown(
        data?.retry_after_seconds ?? MFA_SETTINGS.RESEND_COOLDOWN_SECONDS,
      );
      setResendsRemaining(
        (value) => data?.resends_remaining ?? Math.max(value - 1, 0),
      );
      setCodeLocked(false);
      setInlineError('');
      resetCode();
    } catch (error) {
      if (!isMountedRef.current) return;
      if (handleError(error)) return;
    }
    if (isMountedRef.current) setResending(false);
  };

  // Solo se cierra con "Cancelar": clic fuera o ESC perderían el desafío.
  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onBack();
  };

  const canResend = cooldown === 0 && resendsRemaining > 0;
  const usedResends = resendsRemaining < MFA_SETTINGS.MAX_RESENDS_PER_SESSION;

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      aria-labelledby="mfa-dialog-title"
      PaperProps={{
        component: 'form',
        noValidate: true,
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle id="mfa-dialog-title" sx={{ pb: 1 }}>
        Verificación en dos pasos
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Enviamos un código de 6 dígitos a su correo registrado. Ingréselo
            para continuar. El código es válido por 3 minutos.
          </Typography>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} justifyContent="center">
              {digits.map((digit, index) => (
                <TextField
                  key={`mfa-digit-${index}`}
                  value={digit}
                  autoFocus={index === 0}
                  disabled={codeLocked}
                  error={Boolean(inlineError)}
                  inputRef={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  onChange={(event) =>
                    handleDigitChange(index, event.target.value)
                  }
                  onFocus={(event) => event.target.select()}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    autoComplete: index === 0 ? 'one-time-code' : 'off',
                    'aria-label': `Dígito ${index + 1} del código`,
                    onKeyDown: (event) => handleDigitKeyDown(index, event),
                    onPaste: handlePaste,
                  }}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    '& input': {
                      p: 0,
                      height: { xs: 40, sm: 48 },
                      textAlign: 'center',
                      typography: 'h5',
                    },
                  }}
                />
              ))}
            </Stack>
            {inlineError && (
              <Typography variant="caption" color="error" align="center">
                {inlineError}
              </Typography>
            )}
          </Stack>

          <Stack spacing={0.5} alignItems="center">
            <LoadingButton
              size="small"
              variant={codeLocked && canResend ? 'contained' : 'text'}
              startIcon={<Iconify icon="eva:refresh-fill" />}
              loading={resending}
              disabled={!canResend || verifying}
              onClick={handleResend}
            >
              {cooldown > 0 && resendsRemaining > 0
                ? `Reenviar código (${cooldown} s)`
                : 'Reenviar código'}
            </LoadingButton>
            {usedResends && resendsRemaining > 0 && (
              <Typography variant="caption" color="text.secondary">
                {resendsRemaining === 1
                  ? 'Le queda 1 reenvío.'
                  : `Le quedan ${resendsRemaining} reenvíos.`}
              </Typography>
            )}
            {resendsRemaining === 0 && (
              <Typography variant="caption" color="text.secondary">
                Alcanzó el máximo de reenvíos.
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          color="inherit"
          variant="outlined"
          onClick={onBack}
          disabled={verifying}
        >
          Cancelar
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={verifying}
          disabled={codeLocked || resending || code.length !== CODE_LENGTH}
          endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
        >
          Confirmar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
